import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { useTranslation } from '../hooks/useTranslation';
import { db } from '../utils/db';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import '../styles/MonthlyView.css';

interface HabitRecord {
  habitUuid: string;
  date: string;
  completed: boolean;
  note?: string;
}

const MonthlyView: React.FC = () => {
  const { habitId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const habits = useSelector((state: RootState) => state.habits.habits);

  const [currentDate, setCurrentDate] = useState(new Date());
  const [records, setRecords] = useState<HabitRecord[]>([]);
  const [loading, setLoading] = useState(true);

  const habit = habits.find((h: any) => h.uuid === habitId);

  const loadRecords = useCallback(async () => {
    if (!habitId) return;

    setLoading(true);
    try {
      const start = startOfMonth(currentDate);
      const end = endOfMonth(currentDate);
      const startStr = format(start, 'yyyy-MM-dd');
      const endStr = format(end, 'yyyy-MM-dd');

      // IndexedDB から直接取得
      const monthRecords = await db.records
        .where('habitUuid')
        .equals(habitId)
        .and(r => r.date >= startStr && r.date <= endStr)
        .toArray();

      setRecords(monthRecords.map(r => ({
        habitUuid: r.habitUuid,
        date: r.date,
        completed: r.status === 'completed',
        note: r.note,
      })));
    } catch (error) {
      console.error('Failed to load records:', error);
    } finally {
      setLoading(false);
    }
  }, [habitId, currentDate]);

  useEffect(() => {
    if (!habitId) {
      navigate('/');
      return;
    }
    loadRecords();
  }, [habitId, currentDate, navigate, loadRecords]);

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push({ day: null, date: null });
    }
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      days.push({ day, date });
    }
    return days;
  };

  const getDayStatus = (date: Date | null) => {
    if (!date) return '';
    const dateStr = format(date, 'yyyy-MM-dd');
    const record = records.find(r => r.date === dateStr);
    return record?.completed ? 'completed' : '';
  };

  const navigateMonth = (direction: number) => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + direction, 1));
  };

  const weekDays = [
    t('calendar.sunday'), t('calendar.monday'), t('calendar.tuesday'),
    t('calendar.wednesday'), t('calendar.thursday'), t('calendar.friday'),
    t('calendar.saturday')
  ];

  if (!habit && !loading) {
    return (
      <div className="page-container">
        <p>習慣が見つかりません</p>
        <button onClick={() => navigate('/')}>{t('common.backToHome')}</button>
      </div>
    );
  }

  return (
    <div className="monthly-view-container">
      <div className="monthly-header">
        <button className="back-btn" onClick={() => navigate('/')}>
          ← {t('common.back')}
        </button>
        <h1>{habit?.name || '...'} - {t('calendar.monthlyView')}</h1>
      </div>

      <div className="calendar-container">
        <div className="calendar-navigation">
          <button onClick={() => navigateMonth(-1)} className="nav-btn">←</button>
          <h2>{format(currentDate, 'yyyy MM')}</h2>
          <button onClick={() => navigateMonth(1)} className="nav-btn">→</button>
        </div>

        {loading ? (
          <div className="loading">{t('common.loading')}</div>
        ) : (
          <div className="calendar-grid">
            <div className="weekdays">
              {weekDays.map(day => (
                <div key={day} className="weekday">{day.substring(0, 1)}</div>
              ))}
            </div>

            <div className="days">
              {getDaysInMonth().map((item, index) => {
                const status = getDayStatus(item.date);
                const isToday = item.date?.toDateString() === new Date().toDateString();

                return (
                  <div
                    key={index}
                    className={`day ${status} ${isToday ? 'today' : ''} ${!item.day ? 'empty' : ''}`}
                    style={{ backgroundColor: status === 'completed' ? habit?.color : undefined }}
                  >
                    {item.day && (
                      <>
                        <span className="day-number">{item.day}</span>
                        {status === 'completed' && <span className="check-mark">✓</span>}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MonthlyView;
